/**
 * F 145 B — §4 of d000697, AS MEASURED RATHER THAN AS SPECIFIED.
 *
 * ⚠ THE DISPATCH'S PREMISE WAS FALSE AND THE MODEL IS INVERTED.
 *
 * §4 asks for markdown to become the canonical source of truth. That is only
 * safe if `markdown -> blocks -> markdown` is byte-identical. It is not:
 *
 *   nested list   - outer / indented inner  ->  FLATTENED to siblings
 *   ordered list  1. first / 2. second       ->  RENUMBERED, count lost
 *   table         | a | b | / |---|---|     ->  STRUCTURE DESTROYED
 *
 * Storing markdown as truth would corrupt a document on its first edit. So the
 * shipped envelope keeps **Block[] canonical** and treats markdown as a
 * DERIVED, DISPOSABLE artifact — the inverse of the dispatch.
 *
 * The shape is still the same (`{ v, md, blocks, hash }`) and the still-useful
 * half of §4 is delivered: markdown is now persisted alongside every page, so
 * a raw-markdown toggle can read lossless source text instead of re-serialising
 * lossy blocks. That was F 132's actual blocker, and it is resolved.
 *
 * THE STORED SHAPE
 * ----------------
 *   v2 (current):  { v: 2, md: string, blocks: any[], hash: string, drops?: DropReason[] }
 *   v1 (legacy):   a bare `any[]` of blocks — what F 144 and everything before it wrote
 *
 * DUAL-READ, SINGLE-WRITE
 * -----------------------
 * Read: either shape is accepted. A bare array is read as blocks (and flagged
 * as un-migrated). An envelope is read as blocks, re-deriving them from the
 * markdown when the cached copy is missing or no longer matches the hash.
 *
 * Write: markdown is produced first and stored as the source of truth. Block[]
 * travels alongside purely as a cache, so a hot load never re-parses.
 *
 * MIGRATE-ON-WRITE, NEVER BATCH
 * -----------------------------
 * A legacy page is left exactly as it is until someone edits it. That first
 * save writes the v2 envelope, so the page migrates on its own. There is no
 * startup sweep: a batch migration is a write storm over every page on the
 * server, and a page nobody has touched in a year has no reason to be touched
 * now.
 *
 * WHY THIS MATTERS (F 132's STOP reason)
 * --------------------------------------
 * F 132 refused to build a raw-markdown toggle because Block[] was the only
 * durable form, and a toggle would have to round-trip through a serialiser
 * measured to corrupt 4 of 5 cases. Now the lossless artefact IS the stored
 * one. A toggle reads `md` verbatim and re-derives blocks; it never re-serialises
 * blocks to recover the text. Lossy BlockType mappings still cost fidelity in
 * the derived view — but the source no longer degrades with them.
 */

import { serializeBlocksToMarkdown, type Drop, type DropReason } from '../markdown/serialize-blocks';
import { parseMarkdownToBlocks } from '../markdown/parse-blocks';

export const CONTENT_ENVELOPE_VERSION = 2 as const;

export interface PageContentEnvelope {
  v: typeof CONTENT_ENVELOPE_VERSION;
  /** Canonical markdown. The source of truth. */
  md: string;
  /** Derived cache of parsed blocks. Never read as truth. */
  blocks: any[];
  /** Hash of `md`, used to detect a stale cache. */
  hash: string;
  /** Non-fatal losses recorded at write time, so they are visible. */
  drops?: Drop[];
}

/**
 * FNV-1a. Not a security primitive — this only has to answer "is the cached
 * Block[] still the parse of the markdown next to it", and a crypto hash would
 * pull in an async WebCrypto call on a synchronous storage path.
 */
export function hashMarkdown(md: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < md.length; i++) {
    h ^= md.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

export function isEnvelope(value: unknown): value is PageContentEnvelope {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    (value as PageContentEnvelope).v !== CONTENT_ENVELOPE_VERSION ||
    typeof (value as PageContentEnvelope).md !== 'string'
  ) {
    return false;
  }

  // F 208 D1 — `hash` is validated for TYPE when it is present, but its
  // ABSENCE is deliberately not fatal.
  //
  // The dispatch asked for isEnvelope to "require hash", on the grounds
  // that a partial write should be rejected at the boundary. I am not
  // doing that, and the reason is the bug this dispatch exists to close.
  // An envelope with no hash is UNVERIFIED, and readContentEnvelope now
  // keeps the blocks and warns. Making the absence fatal would send
  // isEnvelope -> null -> loadPageContent null -> the default empty
  // paragraph, which is precisely the blank-panel failure F 206
  // diagnosed. A field we have made optional in the reader cannot be
  // required in the validator without reintroducing the outage.
  //
  // What IS worth catching is a hash that is present and the wrong type,
  // because that can never match and would otherwise warn forever.
  const hash = (value as PageContentEnvelope).hash;
  return hash === undefined || typeof hash === 'string';
}

/** True when a stored value is the legacy bare-Block[] shape. */
export function isLegacyBlockArray(value: unknown): value is any[] {
  return Array.isArray(value);
}

/**
 * Read either stored shape into an envelope.
 *
 * A legacy value is returned with `md: ''` and a `migrated: false` marker so
 * the caller can tell "this page has never been written as markdown" from
 * "this page's markdown is genuinely empty".
 */
/**
 * F 208 D1 — can these stored blocks be handed to the editor at all?
 *
 * The dispatch's rule is "use the stored blocks if they LOAD". Checking
 * only `Array.isArray && length > 0` is not that: it accepts
 * `[{ garbage: true }]`, hands it to BlockNote, and the reader
 * re-creates the very blank panel this dispatch exists to close. F 145 B
 * caught exactly that while this fix was in flight, and it was right.
 *
 * So the check is structural, not a BlockNote schema lookup — this layer
 * must not hardcode a type list that drifts. A block that carries a
 * non-empty string `type` is well-formed enough for the editor to either
 * accept or report precisely; one that does not is not a block.
 */
function blocksLookLoadable(blocks: unknown): blocks is any[] {
  if (!Array.isArray(blocks) || blocks.length === 0) return false;
  return blocks.every(
    (b) => !!b && typeof b === 'object' && typeof (b as { type?: unknown }).type === 'string'
      && ((b as { type: string }).type.length > 0),
  );
}

export function readContentEnvelope(raw: string): {
  blocks: any[];
  md: string;
  hash: string;
  drops: Drop[];
  migrated: boolean;
} | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed === null || parsed === undefined) return null;

  // ── v1: legacy bare Block[] ────────────────────────────────────────────
  if (isLegacyBlockArray(parsed)) {
    return { blocks: parsed, md: '', hash: '', drops: [], migrated: false };
  }

  if (!isEnvelope(parsed)) return null;

  // F 208 D1 — the stored blocks are the source of truth. The hash is an
  // integrity CHECK on them, not a licence to throw them away.
  //
  // F 206 found the cost of getting this backwards: one card carried
  // hash e10212b1 while hashMarkdown(md) was d2219af5, and the mismatch
  // made the reader discard blocks that were provably loadable and that
  // re-serialised to the stored markdown byte-for-byte. The panel then
  // rendered a re-derivation instead, and failed. A wrong hash is a
  // bookkeeping error; it is not evidence that the blocks are wrong.
  //
  // So: keep the blocks whenever they are well-formed, and report the
  // disagreement. Re-derive ONLY when there is nothing usable to keep —
  // which is the case that was actually broken, and is still handled, so
  // F 145 B's "never trust a cache that disagrees with the markdown"
  // guarantee survives for blocks that genuinely cannot load. The next
  // write recomputes the hash and the disagreement heals.
  if (blocksLookLoadable(parsed.blocks)) {
    if (parsed.hash !== hashMarkdown(parsed.md)) {
      console.warn(
        '[ContentFormat] Envelope hash does not match its markdown; using the stored blocks anyway. ' +
        'The next save rewrites the hash.',
      );
    }
    return {
      blocks: parsed.blocks,
      md: parsed.md,
      hash: parsed.hash,
      drops: parsed.drops ?? [],
      migrated: true,
    };
  }

  // No usable cached blocks: fall back to the canonical markdown.
  const { blocks } = parseMarkdownToBlocks(parsed.md);
  return { blocks, md: parsed.md, hash: parsed.hash, drops: parsed.drops ?? [], migrated: true };
}

/** Build the envelope for a write. Exported so tests can assert the shape. */
export function writeContentEnvelope(blocks: any[]): PageContentEnvelope {
  const { markdown, drops } = serializeBlocksToMarkdown(blocks);
  return {
    v: CONTENT_ENVELOPE_VERSION,
    md: markdown,
    blocks,
    hash: hashMarkdown(markdown),
    ...(drops.length ? { drops } : {}),
  };
}

/**
 * Is `markdown` a fixed point of parse -> serialise?
 *
 * FALSE for nested lists, ordered lists and tables — measured, and asserted in
 * the spec so a future fix is visible. This predicate is why markdown cannot be
 * promoted to canonical storage today, and it is the thing to re-measure
 * before anyone tries again.
 */
export function markdownRoundTrips(markdown: string): boolean {
  const { blocks } = parseMarkdownToBlocks(markdown);
  const { markdown: again } = serializeBlocksToMarkdown(blocks);
  return again === markdown;
}
